import React, { memo, useCallback, useEffect, useState } from "react";
import { array, bool, func, object, string } from "prop-types";
import { connect } from "react-redux";
import get from "lodash/get";

import DesktopLogo from "../../../atoms/DesktopLogo";
import Headroom from "../../../layouts/header/UniformHeader/Headroom";
import PaloAuth from "../../../molecules/PaloAuth";

import Icons, { SvgIcon } from "../../../icons";
import { useDeviceType } from "../../../utils/react-utils";
import { staticTexts } from "../../../utils/Translate";
import { normalizeGames } from "../games-data";

import "./games-header-footer.m.css";

const GamesHeader = ({ publisherAttributes, domainSlug, headerDown, loading, games: apiGames = [] }) => {
  const [menuState, setMenuState] = useState("closed");
  const showHam = menuState !== "closed";
  const games = normalizeGames(apiGames);
  const translations = staticTexts(publisherAttributes.lang);

  const toggleMenu = useCallback(() => {
    setMenuState((current) => (current === "closed" ? "open" : "closing"));
  }, []);
  const closeMenu = useCallback(() => setMenuState("closing"), []);

  const { tab, desktop } = useDeviceType();

  useEffect(() => {
    if (loading) headerDown(true);
  }, [loading]);

  useEffect(() => {
    if (!showHam) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeMenu();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showHam, closeMenu]);

  useEffect(() => {
    if (menuState !== "closing") return undefined;

    const timeout = window.setTimeout(() => setMenuState("closed"), 240);
    return () => window.clearTimeout(timeout);
  }, [menuState]);

  return (
    <div className="header">
      <div styleName="base">
        <Headroom
          desktop={desktop || tab}
          desktopHeight={desktop || tab ? 64 : 0}
          downTolerance={desktop || tab ? 0 : 8}
          onPin={() => headerDown(true)}
          onUnpin={() => headerDown(false)}
          onUnfix={() => headerDown(true)}
        >
          <div styleName="head">
            <div styleName="brand">
              <button
                type="button"
                styleName="hamburger-button"
                aria-label={showHam ? "Close games menu" : "Open games menu"}
                aria-expanded={menuState === "open"}
                aria-controls="games-menu"
                onClick={toggleMenu}
              >
                <SvgIcon type="hamburger" styleName="hamburger" />
              </button>
              <a href="/" styleName="mobile-logo" aria-label={translations.prothomAlo}>
                <Icons type="sun" styleName="svg-icon" />
              </a>
              <DesktopLogo styleName="logo" domainSlug={domainSlug} lang={publisherAttributes.lang} />
              <span styleName="divider" aria-hidden="true" />
              <span styleName="games-label">{translations.games}</span>
            </div>
            <div styleName="login">
              <PaloAuth onLogout={() => localStorage.setItem("redirectUrl", `${window.location.origin}/games`)} />
            </div>
          </div>
        </Headroom>
      </div>

      {showHam && (
        <div styleName={`games-menu-overlay games-menu-${menuState}`} className="print-none">
          <button type="button" styleName="games-menu-backdrop" aria-label="Close games menu" onClick={closeMenu} />
          <nav
            id="games-menu"
            styleName="games-menu-panel"
            aria-label={translations.games + " " + translations.gamesMenu}
          >
            <a href="/" styleName="games-menu-item games-menu-home" onClick={closeMenu}>
              <Icons type="sun" styleName="svg-icon" />
              <span styleName="games-menu-home-text">{translations.prothomAloGames}</span>
            </a>
            {games.map((game) => (
              <a
                href={game.game_link || `/games/${game.game_slug}`}
                styleName="games-menu-item"
                key={game.game_id}
                onClick={closeMenu}
              >
                <img src={game.thumbnail_url || game.game_image_url} alt="" />
                <span>{game.game_name}</span>
              </a>
            ))}
          </nav>
        </div>
      )}
    </div>
  );
};

GamesHeader.propTypes = {
  domainSlug: string,
  publisherAttributes: object,
  games: array,
  headerDown: func,
  loading: bool,
};

const mapStateToProps = (state) => ({
  loading: state.pageLoading,
  publisherAttributes: get(state, ["qt", "config", "publisher-attributes"], {}),
  games: get(state, ["qt", "data", "games"], []),
});

const mapDispatchToProps = (dispatch) => ({
  headerDown: (flag) => dispatch({ type: "HEADER_DOWN", payload: flag }),
});

export default connect(mapStateToProps, mapDispatchToProps)(memo(GamesHeader));
